/**
 * ログインページ - AIRGROW Material Interface
 * デザイン: DESIGN.md 参照
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import AuthLayout, { AuthBg, AuthSubmitButton } from '../components/ui/AuthLayout';
import { C } from '../ColorTokens';

// ================================================================
// メインコンポーネント
// ================================================================
export default function Login() {
  const { login, setNewPassword, pendingUser, error } = useAuth();
  const navigate = useNavigate();

  const [email,           setEmail]           = useState('');
  const [password,        setPassword]        = useState('');
  const [showPassword,    setShowPassword]    = useState(false);
  const [newPassword,     setNewPasswordVal]  = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError,      setLocalError]      = useState('');
  const [isLoading,       setIsLoading]       = useState(false);

  // ---- ログイン処理 ----
  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) navigate('/');
    } catch (err) {
      setLocalError(err.message || 'ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- 初回パスワード変更処理 ----
  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (newPassword !== confirmPassword) {
      setLocalError('パスワードが一致しません');
      return;
    }
    if (newPassword.length < 8) {
      setLocalError('パスワードは8文字以上で入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await setNewPassword(newPassword);
      if (result.success) navigate('/');
    } catch (err) {
      setLocalError(err.message || 'パスワード変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ================================================================
  // 初回パスワード変更画面
  // ================================================================
  if (pendingUser) {
    return (
      <div className="h-screen relative overflow-hidden flex items-center justify-center">
        <AuthBg overlay="rgba(0,80,142,0.55)" />

        <div
          className="relative z-10 w-full mx-4 glass-panel"
          style={{
            maxWidth: '440px',
            borderRadius: '24px',
            padding: '40px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <div className="flex flex-col items-center gap-4 mb-10">
            <div
              className="flex items-center justify-center"
              style={{
                width: '56px', height: '56px',
                backgroundColor: C.primaryContainer,
                borderRadius: '16px',
                transform: 'rotate(12deg)',
                boxShadow: '0 8px 24px rgba(0,104,183,0.35)',
              }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                key
              </span>
            </div>
            <div className="text-center">
              <h2 style={{ fontFamily: 'Space Grotesk', fontSize: '28px', fontWeight: 700, color: C.primary, marginBottom: '4px' }}>
                パスワード変更
              </h2>
              <p style={{ fontFamily: 'Noto Sans JP', fontSize: '14px', color: C.onSurfaceVariant }}>
                初回ログインのため、新しいパスワードを設定してください
              </p>
            </div>
          </div>

          <form onSubmit={handleNewPassword} className="space-y-6">
            {(localError || error) && (
              <Alert variant="error">{localError || error}</Alert>
            )}
            <Input label="新しいパスワード" id="new-password" type="password"
              value={newPassword} onChange={e => setNewPasswordVal(e.target.value)}
              placeholder="8文字以上、大小英字・数字含む" icon="lock" autoFocus />
            <Input label="パスワード確認" id="confirm-password" type="password"
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder="パスワード再入力" icon="lock" />
            <AuthSubmitButton loading={isLoading}>パスワードを変更</AuthSubmitButton>
          </form>
        </div>
      </div>
    );
  }

  // ================================================================
  // 通常ログイン画面
  // ================================================================
  return (
    <AuthLayout
      leftHeading={<>FLIGHT<br />PLANNER</>}
      leftBody={<>次世代のドローンマネジメント・プラットフォーム。<br />高度な自律飛行とリアルタイムテレメトリを<br />あなたの手に。</>}
    >
      {/* ログインフォーム */}
      <form onSubmit={handleLogin}>
        <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {(localError || error) && (
            <Alert variant="error">{localError || error}</Alert>
          )}

          <Input
            label="ユーザーID / メールアドレス"
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="example@airgrow.jp"
            icon="person"
            autoFocus
          />

          <Input
            label="パスワード"
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            icon="lock"
            endAdornment={
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="transition-colors"
                style={{ color: C.outline, display: 'flex', alignItems: 'center' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            }
          />
        </div>

        {/* ログイン保持 ＋ パスワード忘れ */}
        <div className="flex items-center justify-between" style={{ marginBottom: '32px' }}>
          <label className="flex items-center gap-2 cursor-pointer">
            <div style={{
              position: 'relative', width: '40px', height: '20px',
              backgroundColor: C.surfaceContainerHighest,
              borderRadius: '9999px',
            }}>
              <div style={{
                position: 'absolute', left: '4px', top: '4px',
                width: '12px', height: '12px',
                backgroundColor: '#fff',
                borderRadius: '9999px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
            <span style={{ fontSize: '13px', color: C.onSurfaceVariant, fontFamily: 'Noto Sans JP' }}>
              ログイン状態を保持
            </span>
          </label>
          <a
            href="#"
            style={{ fontSize: '13px', fontWeight: 500, color: C.primary, fontFamily: 'Noto Sans JP', textDecoration: 'none' }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}
          >
            パスワードを忘れた場合
          </a>
        </div>

        <AuthSubmitButton loading={isLoading}>ログイン</AuthSubmitButton>
      </form>

      {/* 新規登録リンク */}
      <div
        style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: `1px solid ${C.surfaceContainerHighest}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <p style={{ fontSize: '13px', color: C.onSurfaceVariant, fontFamily: 'Noto Sans JP' }}>
          アカウントをお持ちでないですか？
        </p>
        <Link
          to="/register"
          className="flex items-center gap-0.5"
          style={{ fontSize: '13px', fontWeight: 500, color: C.primary, fontFamily: 'Noto Sans JP', textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
          新規登録
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
        </Link>
      </div>
    </AuthLayout>
  );
}
